import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import VerifyComponent from "./verify.component.jsx";
import { useAppDispatch } from "../../hooks/reduxHooks.js";
import { verifyTenantApi, resendVerificationApi } from "../../services/register.service.js";
import { buildTenantLoginUrl } from "../../utils/tenantFromHost.js";

const VerifyContainer = () => {
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const [countdown, setCountdown] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  const verifyToken = searchParams.get("token");

  // Decode the JWT token to get the slug and email
  let slug = null;
  let orgEmail = null;
  if (verifyToken) {
    try {
      const payload = JSON.parse(atob(verifyToken.split('.')[1]));
      slug = payload.slug;
      orgEmail = payload.org_email;
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }

  useEffect(() => {
    if (!verifyToken) {
      message.error("Invalid verification session. Please sign up again.");
      navigate("/signup", { replace: true });
    }
  }, [verifyToken, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleVerify = (otp) => {
    setSubmitting(true);

    const payload = {
      token: verifyToken,
      code: otp,
    };

    dispatch(
      verifyTenantApi({
        params: payload,
        finalCallback: () => {
          setSubmitting(false);
        },
        successCallback: (res) => {
          message.success("Email verified successfully! Please login to continue.");
          // Clear saved signup data
          sessionStorage.removeItem('signupData');
          window.location.href = buildTenantLoginUrl({ slug, search: "ref=signup" });
        },
        failureCallback: (error) => {
          message.error(
            error?.response?.data?.message || "Verification failed. Please try again."
          );
        },
      })
    );
  };

  const handleCorrectEmail = () => {
    navigate('/signup');
  };

  const handleResend = () => {

    const payload = {
      token: verifyToken,
    };

    dispatch(
      resendVerificationApi({
        params: payload,
        finalCallback: () => {
          setResending(false);
        },
        successCallback: (res) => {
          // Assuming the response contains seconds remaining
          const seconds = res.seconds || res.remainingTime || 60; // Default to 60 if not provided
          setCountdown(seconds);
          setErrorMessage(""); // Clear any previous error
          message.success("Verification code resent successfully!");
        },
        failureCallback: (error) => {
          console.error("Resend failed:", error);

          const errorMsg = error?.response?.data?.detail;
          if (!isNaN(parseInt(errorMsg))) {
            setCountdown(parseInt(errorMsg));
            setErrorMessage("");
            message.error(`Please wait ${parseInt(errorMsg)} seconds before resending.`);
          } else {
            setCountdown(-1);
            setErrorMessage(errorMsg);
            message.error(
              error?.response?.data?.message || "Failed to resend verification code."
            );
          }
        },
      })
    );
  };

  if (!verifyToken) {
    return null;
  }

  return (
    <VerifyComponent
      handleVerify={handleVerify}
      handleResend={handleResend}
      handleCorrectEmail={handleCorrectEmail}
      submitting={submitting}
      resending={resending}
      countdown={countdown}
      errorMessage={errorMessage}
      orgEmail={orgEmail}
    />
  );
};

export default VerifyContainer;
